import type { TypeNavEntity, TypeNavNode, TypeSearchHit } from '@/types/ui/types.ui.navigation';
import navigationFlatten from './navigation.flatten';

const normalize = (value: string) => value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

function score(haystack: string, query: string): number {
    const text = normalize(haystack);
    if (text === query) return 100;
    if (text.startsWith(query)) return 80;
    if (text.split(/[\s/·.-]+/).some((word) => word.startsWith(query))) return 60;
    if (text.includes(query)) return 30;
    return 0;
}

export default function navigationSearch(
    nodes: TypeNavNode[],
    entities: TypeNavEntity[],
    rawQuery: string,
    limit = 12,
): TypeSearchHit[] {
    const query = normalize(rawQuery.trim());
    if (!query) return [];

    const flat = navigationFlatten(nodes);
    const trailById = new Map(flat.map((entry) => [entry.node.id, entry.trail]));

    const pages: TypeSearchHit[] = flat.flatMap(({ node, trail }) => {
        const best = Math.max(score(node.label, query), score(node.description, query) * 0.5);
        return best > 0 ? [{ type: 'page', score: best + 5, node, trail }] : [];
    });

    const rows: TypeSearchHit[] = entities.flatMap((entity) => {
        const best = Math.max(
            score(entity.label, query),
            score(entity.hint, query) * 0.6,
            ...(entity.keywords ?? []).map((keyword) => score(keyword, query) * 0.9),
        );
        const trail = trailById.get(entity.parentId) ?? [];
        return best > 0 ? [{ type: 'entity', score: best, entity, trail }] : [];
    });

    return [...pages, ...rows]
        .sort((a, b) => b.score - a.score)
        .slice(0, limit);
}
